import { useToast } from '@/hooks/use-toast'
import http from '@/lib/http'
import type { AdditionalNotesType, MedRequest } from '@/types/data'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@/services/queryClient'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useFilterStore } from '@/lib/stores/filter'
import { resetMedicationRequestStore, useMedicationRequestStore } from '@/lib/stores/medication-request'
import { useConsultationAISummary } from '@/hooks/useConsultationAISummary'
import { isUserTrialPrescriber } from '@/lib/permissions'
import { useUtilityStore } from '@/lib/stores/utils'

export const invalidateRelevantActionQueries = (patientId: string, medReqId: string) => {
  queryClient.invalidateQueries({
    queryKey: ['patientOrderHistory', patientId]
  })
  queryClient.invalidateQueries({
    queryKey: ['medicationRequestById', medReqId]
  })

  queryClient.invalidateQueries({ queryKey: ['medRequestFiltering'] })
}

export type MedRequestMutationFnProps = {
  id: string
  action: 'approve' | 'reject' | 'hold' | 'skip'
  reason?: string
  changeProduct?: {
    prevSKU: string
    newSKU: string
  } | null
  approveDate?: string
  additionalNotes?: AdditionalNotesType
  extraDirectionsForMedication?: string
  scrExecutedPrompt?: string
  scrSummaryResult?: string
  startConversation?: boolean
  csNeedToContactPatient?: boolean
  /** Set on sibling actions of any family decision (MPS or multi product order) so it raises a single CS email. */
  suppressCsEmailForMps?: boolean
  /** LINK orders only. */
  isTemporaryReplacement?: boolean
  isLastSubscriptionOrder?: boolean
}

const useMedRequestAction = (props?: {
  doNotResetStore?: boolean
  isBatch?: boolean
  enabled?: boolean
  patientOrigin?: 'medication-request' | 'investigation'
}) => {
  const { toast } = useToast()
  const currentMedication = useCurrentMedication(props?.enabled)
  const { data: aiOutput } = useConsultationAISummary(currentMedication?.data)
  const { safeguardingQuestions } = useMedicationRequestStore()

  const isTrialPrescriber = isUserTrialPrescriber()

  return useMutation({
    mutationKey: ['medRequestAction'],
    mutationFn: ({
      id,
      action,
      reason,
      changeProduct,
      approveDate,
      additionalNotes,
      extraDirectionsForMedication,
      scrExecutedPrompt,
      scrSummaryResult,
      startConversation,
      csNeedToContactPatient,
      suppressCsEmailForMps,
      isTemporaryReplacement,
      isLastSubscriptionOrder
    }: MedRequestMutationFnProps) => {
      // Only perform actual mutation actions if the user is not a trial prescriber.
      if (isTrialPrescriber) {
        const { actionedMedicationRequestIds, setActionedMedicationRequestIds } = useUtilityStore.getState()
        setActionedMedicationRequestIds(Array.from(new Set([...actionedMedicationRequestIds, id])))

        const queryKey = useFilterStore.getState().getCacheKey()
        queryClient.invalidateQueries({
          queryKey
        })
        queryClient.invalidateQueries({
          queryKey: ['totalMedicationRequests']
        })

        return Promise.resolve(null)
      }

      return http.post('/prescriber/med-requests/update', {
        id,
        action,
        additionalNotes,
        extraDirectionsForMedication,
        reason,
        changeProduct,
        approveDate,
        safeguardingQuestions,
        startConversation,
        csNeedToContactPatient,
        suppressCsEmailForMps,
        isTemporaryReplacement,
        isLastSubscriptionOrder,
        scrExecutedPrompt,
        scrSummaryResult,
        ...(aiOutput
          ? {
              consultationAiOutput: {
                prompt: aiOutput.prompt,
                summary: aiOutput.summary
              }
            }
          : {})
      })
    },
    onMutate(variables) {
      queryClient.setQueriesData(
        { queryKey: ['medRequestFiltering'] },
        (old: { data: MedRequest[]; total: number }) => ({
          ...old,
          data: old.data.filter(e => e.id !== variables.id)
        })
      )

      if (!props?.doNotResetStore) {
        resetMedicationRequestStore()
      }
    },
    onSuccess() {
      if (!props?.isBatch && currentMedication?.data) {
        invalidateRelevantActionQueries(currentMedication.data.patient.id, currentMedication.data.id)
      }
    },
    onError() {
      toast({
        title: 'Error',
        description: 'Error taking an action on medication request'
      })
    }
  })
}

export default useMedRequestAction
